import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Square, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api, streamSummarize, type SummarizeKind } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { pushEvent } from "@/lib/analytics";
import { cn } from "@/lib/utils";

interface Props {
  kind: SummarizeKind;
  id?: string | number;
  /** Button label override. */
  label?: string;
  className?: string;
  size?: "sm" | "md";
}

/**
 * Reusable "Résumer avec l'IA" button. Streams a model-generated summary of a
 * thread / forum / news article / stats view into an inline panel. Requires the
 * user to be logged in AND to have configured a BYOK LLM key (otherwise it
 * points them to the account page).
 */
export function SummarizeButton({ kind, id, label = "Résumer avec l'IA", className, size = "sm" }: Props) {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [meta, setMeta] = useState<{ title: string; provider: string; model: string } | null>(null);
  const [status, setStatus] = useState<"idle" | "streaming" | "done" | "error" | "nokey">("idle");
  const [error, setError] = useState("");
  const abortRef = useRef<AbortController | null>(null);

  // Only fetch key status once the user wants to use the feature.
  const { data: keyStatus } = useQuery({
    queryKey: ["llm-key-status"],
    queryFn: () => api.llmKeyStatus(),
    enabled: isAuthenticated && open,
    staleTime: 30_000,
  });

  const hasKey = (keyStatus?.keys.length ?? 0) > 0;

  async function run() {
    pushEvent("summarize", { summarize_kind: kind, target_id: id != null ? String(id) : undefined });
    setOpen(true);
    setText("");
    setError("");
    setMeta(null);
    // Wait a tick so keyStatus can load; if we already know there's no key, bail.
    if (keyStatus && !hasKey) {
      setStatus("nokey");
      return;
    }
    setStatus("streaming");
    const ac = new AbortController();
    abortRef.current = ac;
    await streamSummarize(
      { kind, id },
      {
        signal: ac.signal,
        onMeta: (m) => setMeta(m),
        onChunk: (t) => setText((prev) => prev + t),
        onDone: () => setStatus((s) => (s === "error" ? s : "done")),
        onError: (err) => {
          if (err.includes("no_key") || err.includes("Aucune clé")) setStatus("nokey");
          else {
            setError(err);
            setStatus("error");
          }
        },
      },
    );
  }

  function stop() {
    abortRef.current?.abort();
    setStatus((s) => (s === "streaming" ? "done" : s));
  }

  if (!isAuthenticated) return null;

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {!open || status === "idle" ? (
        <Button
          size={size === "sm" ? "sm" : "default"}
          variant="outline"
          onClick={run}
          className="w-fit"
        >
          <Sparkles className="size-4" />
          {label}
        </Button>
      ) : null}

      {open && status !== "idle" && (
        <Card className="border-primary/30">
          <CardContent className="p-3">
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-xs font-medium text-foreground-muted">
                {meta ? `${meta.title} · ${meta.provider}/${meta.model}` : "Préparation…"}
              </span>
              <div className="flex items-center gap-1">
                {status === "streaming" && (
                  <Button size="sm" variant="ghost" onClick={stop} title="Arrêter">
                    <Square className="size-3.5" />
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    abortRef.current?.abort();
                    setOpen(false);
                    setStatus("idle");
                  }}
                  title="Fermer"
                >
                  <X className="size-3.5" />
                </Button>
              </div>
            </div>

            {status === "nokey" ? (
              <p className="text-sm text-foreground-muted">
                Pour utiliser les résumés IA, ajoutez votre clé API dans{" "}
                <Link to="/account" className="text-primary hover:underline">
                  votre compte
                </Link>
                .
              </p>
            ) : status === "error" ? (
              <p className="text-sm text-red-500">Erreur : {error}</p>
            ) : (
              <p className="whitespace-pre-wrap text-sm leading-relaxed">
                {text}
                {status === "streaming" && <span className="animate-pulse">▌</span>}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
